import { httpDelete, httpGet, httpPatch, httpPost } from "@/api/connection";
import {
  BulkCreateFilesFn,
  CompleteMultipartUploadFn,
  CreateFileFn,
  DeleteFileFn,
  File,
  FileLog,
  GetFilePathFn,
  GetFilesFn,
  GetFilesQuery,
  StartMultipartUploadFn,
  UpdateFileFn,
} from "./files.types";

const getFiles: GetFilesFn = async (q) => {
  const { uuid, ...rest } = q;

  if (uuid) {
    return httpGet(`/files/${uuid}`, rest);
  }

  return httpGet("/files", rest);
};

const getFilePath: GetFilePathFn = async (q) => {
  const { uuid, ...rest } = q;

  return httpGet(`/files/${uuid}/path`, rest);
};

const createFile: CreateFileFn = async (data) => {
  return httpPost("/files", data);
};

const bulkCreateFiles: BulkCreateFilesFn = async (data) => {
  return httpPost("/files/bulk", data);
};

const updateFile: UpdateFileFn = async (data) => {
  return httpPatch(`/files/${data.uuid}`, data);
};

const deleteFile: DeleteFileFn = async (data) => {
  return httpDelete(`/files/${data.uuid}`);
};

const startMultipartUpload: StartMultipartUploadFn = async (data) => {
  return httpPost("/files/start-multipart", data);
};

const completeMultipartUpload: CompleteMultipartUploadFn = async (data) => {
  return httpPost("/files/complete-multipart", data);
};

const downloadFiles = async (data: {
  uuids: string[];
  tenant_slug: string;
  parent_uuid?: string;
}) => {
  let url = `/api/files/download-files?tenant_slug=${data.tenant_slug}&uuids[]=${data.uuids}`;
  if (data.parent_uuid) {
    url += `&parent_uuid=${data.parent_uuid}`;
  }
  const a = document.createElement("a");
  a.href = url;
  a.click();
  a.remove();
};

const shareItems = async (values: {
  items: {
    name: string;
    uuid: string;
    type: "file" | "folder";
    share_data: {
      message?: string;
    };
  }[];
  emails: string[];
  case_uuid?: string;
  message?: string;
}) => {
  return httpPost(`/files/share-items`, values) as Promise<{
    success: boolean;
    message: string;
  }>;
};

const unShareItems = async (values: { uuid: string; email: string }) => {
  return httpPost(`/files/unshare-items`, values) as Promise<{
    success: boolean;
    message: string;
  }>;
};

const getShareDetails = async (values: { uuid: string }) => {
  return httpGet(`/files/share-details`, values) as Promise<{
    data: any[];
  }>;
};

const getLogs = async (values: { uuid: string }) => {
  return httpGet(`/files/logs`, values) as Promise<{
    data: FileLog[];
  }>;
};

const getUsers = async (values?: { case_uuid?: string }) => {
  return httpGet(`/files/users`, values) as Promise<{
    data: File["created_by"];
  }>;
};

const getFileExtensions = async (values?: { case_uuid?: string }) => {
  return httpGet(`/files/extensions`, values) as Promise<{
    data: File["ext"];
  }>;
};

const exportFilesList = async (values: {
  query: GetFilesQuery;
  columns: {
    dataField: string;
    header: string;
    caption: string;
    visible?: boolean;
    dataType?: string;
  }[];
  date_format: string;
  timezone: string;
}) => {
  return httpPost(`/files/export-list`, values) as Promise<{
    signedUrl: string;
    filename: string;
  }>;
};

export const FilesAPI = {
  get: getFiles,
  getFilePath: getFilePath,
  create: createFile,
  update: updateFile,
  delete: deleteFile,
  startMultipartUpload,
  completeMultipartUpload,
  bulkCreate: bulkCreateFiles,
  downloadFiles,
  shareItems,
  unShareItems,
  getShareDetails,
  getLogs,
  getUsers,
  getFileExtensions,
  exportFilesList,
};
